import React, {useEffect, useRef, useState} from 'react';
import {useLocation} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Stack, styled} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import CancelSubscriptionModal from '@contentlab/components/cancel-subscription-modal/CancelSubscriptionModal';
import {ROUTES} from '@contentlab/config/routesConstants';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useStoreFlowStartedEvent from '@contentlab/hooks/analytics/use-store-flow-started-event';
import useStoreViewDismissedEvent from '@contentlab/hooks/analytics/use-store-view-dismissed-event';
import useStoreViewPresentedEvent from '@contentlab/hooks/analytics/use-store-view-presented-event';
import useContactSales from '@contentlab/hooks/use-contact-sales';
import useOnboardingFlags from '@contentlab/hooks/use-onboarding-flags/useOnboardingFlags';
import {contentLabActions, ErrorKind} from '@contentlab/stores/contentLabStore';
import getActiveSubscription from '@contentlab/utils/getActiveSubscription';
import onboardingWizardStore from '@contentlab/views/contentlab/pages/onboarding-page/onboardingWizardStore';
import {ContactSalesButton} from '@contentlab/views/contentlab/pricing/components/pricing-bundle/Buttons';
import {
  getBundleTranslatedName,
  getPlanVariant,
  isFreePlan,
  isTrialPlan,
} from '@contentlab/views/contentlab/pricing/utils';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import Sentry from '@/services/sentry/Sentry';
import {
  CONTENT_LAB_VIEW_NAMES,
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_PRICING_VIEW_TYPES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {removeContentLabWebPaymentFlowId} from '@/utils/LtidStorage';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import ContentLabPlanBundle from '@/types/models/pricing/contentLabPlanBundle';
import {ContentLabSubscription} from '@/types/models/pricing/contentLabSubscription';
import ContentLabLogo from '@/components/ContentLabLogo';
import Link from '@/components/link';
import useContentStudioCancelSubscriptionMutation from '@/hooks/content-studio/mutations/useContentStudioCancelSubscriptionMutation';
import useCurrentSubscriptionQuery from '@/hooks/content-studio/use-current-subscription-query';
import useNavigation from '@/hooks/use-navigation';
import usePrevious from '@/hooks/use-previous';
import styles from './ContentLabPricingPage.module.scss';

export type ContentLabPricingPageProps = {
  flags: {
    ltxScalerFreeTrial: boolean;
  };
};

function BottomSubscriptionInfo({
  activeSubscription,
  cancelSubscriptionHandler,
}: {
  activeSubscription?: ContentLabSubscription;
  cancelSubscriptionHandler: () => void;
}) {
  if (!activeSubscription || isFreePlan(activeSubscription)) return null;
  const isCancelledSubscription = !activeSubscription.isAutoRenewEnabled;
  if (isCancelledSubscription) {
    return (
      <div className={styles.bottomBar}>
        <Body size="md">
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: translate('full-access-until', {
                date: new Date(activeSubscription.renewAtMs).toLocaleDateString(
                  'en-US',
                  {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  }
                ),
              }),
            }}
          />
        </Body>
      </div>
    );
  }
  return (
    <div className={styles.bottomBar}>
      <Body size="md">{translate('Want to end your subscription?')}</Body>
      <Body
        size="md"
        className={styles.clickableText}
        onClick={cancelSubscriptionHandler}
      >
        {translate('Cancel now')}
      </Body>
    </div>
  );
}

const skippedFlowStartedEventSources = [
  CONTENT_STUDIO_PRICING_SOURCES.PRICING_MODAL,
];

const Header = styled(Stack)(() => ({
  width: '100%',
  gap: '8px',
  marginBottom: '36px',
  justifyContent: 'center',
}));

const MainContainer = styled(Stack)(() => ({
  width: '100%',
  justifyContent: 'center',
  maxHeight: '100%',
}));

function ContentLabPricingPage(_props: ContentLabPricingPageProps) {
  const navigation = useNavigation();
  const location = useLocation();
  const previousLocation = usePrevious(location);
  const presentationIdRef = useRef(uuid());

  const {storeFlowStarted} = useStoreFlowStartedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewPresented} = useStoreViewPresentedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {storeViewDismissed} = useStoreViewDismissedEvent(
    FLOW_NAMES.CONTENT_STUDIO.PRICING
  );
  const {uiInteraction} = useUiInteractionEvent();

  const {data: currentSubscriptions} = useCurrentSubscriptionQuery();
  const activeSubscription = getActiveSubscription(
    currentSubscriptions?.subscriptions
  );
  const activePlanBundle = getPlanVariant(activeSubscription);
  const {
    mutateAsync: cancelSubscription,
    isLoading: isCancelSubscriptionInProgress,
  } = useContentStudioCancelSubscriptionMutation();

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const {hasSeenCurrentPricingPageVersion} = useOnboardingFlags();
  const forceOnboarding = onboardingWizardStore(
    useShallow((state) => state.forceOnboarding)
  );
  const isOnLaunch = !hasSeenCurrentPricingPageVersion || forceOnboarding;

  const onContactSalesClick = useContactSales({
    flowName: 'pricing-page-contact-sales',
    interactionLocation: 'pricing-page',
  });

  useEffect(() => {
    removeContentLabWebPaymentFlowId();

    const flow = AnalyticsService.getOrCreateFlow(
      FLOW_NAMES.CONTENT_STUDIO.PRICING
    );
    if (!flow.source || !skippedFlowStartedEventSources.includes(flow.source)) {
      storeFlowStarted({
        flow_id: flow.flow_id,
        product_type: 'subscription',
        source: isOnLaunch ? 'subscription_on_launch' : flow.source || '',
      });
    }

    storeViewPresented({
      presentation_id: presentationIdRef.current,
      view_name: CONTENT_LAB_VIEW_NAMES.PRICING,
      view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
    });

    return () => {
      storeViewDismissed({
        presentation_id: presentationIdRef.current,
        view_name: CONTENT_LAB_VIEW_NAMES.PRICING,
        view_type: CONTENT_STUDIO_PRICING_VIEW_TYPES.PRICING_PAGE,
      });
    };
  }, []);

  const renewAtMs = activeSubscription?.renewAtMs;
  const subscriptionCycleEndDate = renewAtMs ? new Date(renewAtMs) : null;

  const handleBackClick = () => {
    const previousPath = previousLocation?.pathname;
    if (
      previousPath?.startsWith(ROUTES.CONTENT_LAB) &&
      previousPath !== `${ROUTES.CONTENT_LAB}${ROUTES.CONTENT_LAB_PRICING}`
    ) {
      navigation.goBack();
    } else {
      navigation.navigate(ROUTES.CONTENT_LAB);
    }
  };

  const handleCancelSubscription = async () => {
    const showErrorToast = () => {
      contentLabActions.addError({
        title: 'Error',
        description: 'Failed to cancel subscription, please contact support',
        kind: ErrorKind.FAILURE,
      });
    };
    try {
      if (!activeSubscription?.id) {
        showErrorToast();
        return;
      }

      await cancelSubscription(activeSubscription.id);

      raiseFlashMessage({
        status: 'success',
        mode: 'light',
        message: isTrialPlan(activeSubscription) ? (
          <Stack direction="column" gap={0.5}>
            <Headline size="xs">{translate('Free Trial Canceled')}</Headline>
            <Body size="md">
              {translate(
                'You’ll retain access to all credits until the end of your current billing cycle.'
              )}
            </Body>
          </Stack>
        ) : (
          translate('Subscription cancelled successfully')
        ),
      });
    } catch (error) {
      showErrorToast();
      if (error instanceof Error) {
        Sentry.captureException(error, {
          title: 'Error cancelling subscription',
          activeSubscription,
        });
      }
    } finally {
      setIsCancelModalOpen(false);
    }
  };

  return (
    <div className={styles.pricingPage} data-testid="content-lab-pricing-page">
      <div
        className={styles.topBar}
        style={{
          justifyContent: isOnLaunch ? 'flex-end' : 'space-between',
          marginBottom: isOnLaunch ? '0px' : '',
        }}
      >
        {!isOnLaunch && (
          <Button
            appearance="neutral"
            mode="plain"
            size="medium"
            className={styles.backButton}
            icon={
              <Icon
                size="medium"
                appearance="neutral"
                name="Actions-Arrow-Back-Small"
              />
            }
            onClick={handleBackClick}
          >
            {translate('Back')}
          </Button>
        )}
        {isOnLaunch ? (
          <Button
            appearance="neutral"
            mode="plain"
            size="large"
            onClick={() => {
              navigation.navigate(ROUTES.CONTENT_LAB);
              uiInteraction({
                item_name: 'skip',
                interaction_location: CONTENT_LAB_VIEW_NAMES.PRICING,
                flow_name: FLOW_NAMES.CONTENT_STUDIO.PRICING,
                presentation_id: presentationIdRef.current,
                interaction_id: uuid(),
              });
            }}
            className={styles.skipButton}
          >
            {translate('Skip')}
          </Button>
        ) : (
          <Link to={ROUTES.CONTENT_LAB} className={styles.CSLogo}>
            <ContentLabLogo />
          </Link>
        )}
      </div>
      <Header>
        <Headline size="xl" align="center">
          {translate('Find the plan that’s right for you')}
        </Headline>
        <Body
          size="xl"
          align="center"
          color={designSystemToken('semantic.fg.secondary')}
        >
          {translate(
            'Contact sales to buy or upgrade LTX Scaler. Your current subscription keeps working.'
          )}
        </Body>
      </Header>
      <MainContainer>
        <div className={styles.salesCta}>
          <ContactSalesButton onContactSalesClick={onContactSalesClick} />
        </div>
        <BottomSubscriptionInfo
          activeSubscription={activeSubscription}
          cancelSubscriptionHandler={() => setIsCancelModalOpen(true)}
        />
      </MainContainer>

      <CancelSubscriptionModal
        open={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelSubscription}
        planName={getBundleTranslatedName(
          activePlanBundle ?? ContentLabPlanBundle.FREE
        )}
        cycleEndDate={subscriptionCycleEndDate}
        isLoading={isCancelSubscriptionInProgress}
        primaryButtonClassName={styles.cancelSubscriptionButton}
        contentClassName={styles.modalContent}
        source={CONTENT_LAB_VIEW_NAMES.PRICING}
        isTrial={isTrialPlan(activeSubscription)}
      />
    </div>
  );
}

export default ContentLabPricingPage;
